<template>
    <div class="all-options-container">
        <div class="form-row" v-if="fileFormatOptions.length > 1 || tableTypeOptions.length > 1">
            <div class="form-row-item" v-if="fileFormatOptions.length > 1">
                <span class="label-text">文件格式</span>
                <a-select
                    v-model:value="formData.fileFormat"
                    class="select-item"
                    :options="fileFormatOptions"
                    @change="fileFormatChange"
                ></a-select>
            </div>
            <div class="form-row-item" v-if="tableTypeOptions.length > 1">
                <span class="label-text">表格内容展示形式</span>
                <a-select
                    v-model:value="formData.otherConfig.tableStyle"
                    class="select-item" 
                    :options="tableTypeOptions"
                    @change="tableTypeChange"
                ></a-select>
            </div>
            <div class="form-row-item" v-if="showMarkedStatus">
                <span class="label-text">标注状态</span>
                <a-select
                    v-model:value="formData.approveStatusList"
                    allowClear
                    placeholder="请选择标注状态"
                    mode="multiple"
                    class="select-item" 
                    :maxTagCount="1"
                    :options="statusOptions"
                ></a-select>
            </div>
        </div>
        <div class="form-row" v-if="filteredOptions.length || hasSearchOptions">
            <div class="export-modal-title">
                可选字段（共{{ totalCount }}个）
            </div>
        </div>
        <div class="form-row" v-if="filteredOptions.length || hasSearchOptions">
            <a-checkbox
                v-model:checked="allState.checkAll"
                :indeterminate="allState.indeterminate"
                @change="onCheckAll">
                <span class="check-all">全选</span>
            </a-checkbox>
            <a-input-search class="input-search"
                v-model:value.trim="searchValue"
                allowClear
                :maxlength="50"
                placeholder="请输入可选字段"
                @search="filterExportItems" />
        </div>
        <div class="all-options-content" v-if="filteredOptions.length || hasSearchOptions">
            <a-collapse :bordered="false" v-model:activeKey="activeKey">
                <template #expandIcon="{ isActive }">
                    <span>
                        <CaretRightOutlined class="custom-collapse-icon" :class="{'rotate-deg': isActive}"/>
                    </span>
                </template>
                <a-collapse-panel v-for="item of filteredOptions" :key="item.nodeCode">
                    <template #header>
                        <div class="all-check" @click.stop>
                            <a-checkbox
                                v-model:checked="item.checkAll"
                                :indeterminate="item.indeterminate"
                                @change="(value) => checkChildAllAndUpdateSelected(value, item)">
                                <span class="check-all">{{ item.nodeName }}</span>
                            </a-checkbox>
                        </div>
                    </template>

                    <a-checkbox-group v-model:value="item.checkedList" @change="() => changeOption(item)" class="checkbox-group">
                        <template v-for="option in item.sysExportNodeColumnList" :key="option.nodeCode">
                            <a-checkbox :value="option.nodeCode" :disabled="option.disable" @change="(value) => changeInnerOption(value, option, item)">
                                <span class="checkbox-item" :title="option.nodeName">{{ option.nodeName }}</span>
                            </a-checkbox>
                        </template>
                    </a-checkbox-group>
                </a-collapse-panel>
            </a-collapse>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { reactive, ref, toRaw, watch, computed, } from 'vue';
import { CaretRightOutlined } from '@ant-design/icons-vue';
import { cloneDeep } from 'lodash-es';

const props = defineProps({
    selected: {
        type: Array,
    },
    options: {
        type: Array,
    },
    fileFormatList: {
        type: Array,
        default: () => []
    },
    showTableFormat: {
        type: Boolean,
        default: false
    },
    showMarkedStatus: {
        type: Boolean,
        default: false
    },
});

const emit = defineEmits(['update:selected', 'update:fileForm', 'optionSelected', 'fileFormatChange']);

const allOptions = ref([]);
const activeKey = ref([]);
const totalCount = ref(0);
const searchValue = ref('')
const filteredOptions = ref([]);
const formData = ref({
    fileFormat: null,
    otherConfig: {
        tableStyle: null,
    },
    approveStatusList: [],

});

const fileFormatOptions = computed(() => {
    return props.fileFormatList
});

// 表格内容展示形式
const tableTypeOptions = ref([])
const hasSearchOptions = ref(false);
const fileFormatChange = (value) => {
    searchValue.value = ''
    hasSearchOptions.value = false
    emit('fileFormatChange', value);
    emit('update:fileForm', formData.value)
}
const tableTypeChange = (value) => {
    emit('update:fileForm', formData.value)
}

const defaultStatusOptions = [
    {
        label: '审核通过',
        value: 'approve_pass',
    },
    {
        label: '审核未通过',
        value: 'un_approve_pass',
    },
    {
        label: '审核中',
        value: 'in_approve',
    },
    {
        label: '未标注',
        value: 'un_annotated',
    }
]

const statusOptions = computed(() => {
    let options =cloneDeep(defaultStatusOptions);
    if (formData.value.fileFormat === 'json') {
        options = options.filter(item => item.value !== 'un_annotated');
    }
    formData.value.approveStatusList = options.map(item => item.value);
    return options
})

const setFileFormat = () => {
    if (props.fileFormatList?.length) {
        let fileFormatItem = props.fileFormatList[0];
        if (!formData.value.fileFormat) {
            formData.value.fileFormat = props.fileFormatList[0]?.value;
        } else {
            fileFormatItem = props.fileFormatList.find(item => item.value === formData.value.fileFormat);
        }
        setTableTypeOptions(fileFormatItem?.otherConfig?.tableStyle ?? [])
    } else {
        formData.value.fileFormat = null;
    }
}

const setTableTypeOptions = (tableList) => {
    tableTypeOptions.value = tableList?.map(item => {
        return {
            label: item.name,
            value: item.value,
        }
    }) || [];
    if (tableTypeOptions.value.length) {
        formData.value.otherConfig.tableStyle = tableTypeOptions.value[0]?.value;
    } else {
        formData.value.otherConfig.tableStyle = null;
    }
    emit('update:fileForm', formData.value)
}

// 设置选中项，判断当前展示与选中项，给外层数据添加checkedList
const setOptionSelected = () => {
  filteredOptions.value.forEach(item => {
    const outerOption = props.selected?.find(selectedOuterItem => selectedOuterItem.nodeCode === item.nodeCode);
    if (outerOption) {
      item.checkedList = outerOption.sysExportNodeColumnList?.filter(d => 
          d.disable || item.sysExportNodeColumnList?.some(s => s.nodeCode === d.nodeCode)
        )
        .map(d => d.nodeCode)
    } else {
      item.checkedList = item.sysExportNodeColumnList.filter(d => d.disable).map(d => d.nodeCode);
    }

    changeItemChecked(item)
    checkStatus(item)
  })

  checkAllStatus()
}

// 只对二级数据进行搜索过滤
const filterExportItems = () => {
    const tempActiveKeys = [];
    totalCount.value = 0;
    hasSearchOptions.value = !!searchValue.value;
    filteredOptions.value = allOptions.value?.reduce((preValue, currentValue) => {
        const childrenNodes = currentValue.sysExportNodeColumnList;
        if (childrenNodes?.length) {
            const filteredChildren = childrenNodes.filter(item => item.nodeName?.includes(searchValue.value));
            if (filteredChildren?.length) {
                totalCount.value += filteredChildren.length;
                const newOuterNode = {...currentValue, sysExportNodeColumnList: filteredChildren};
                tempActiveKeys.push(currentValue.nodeCode)
                return preValue.concat(newOuterNode)
            }
        }

        return preValue;
    }, [])

    if (!filteredOptions.value.length) {
        allState.checkAll = false;
        allState.indeterminate = false;
    }

    // 要全部折叠都展开
    activeKey.value = tempActiveKeys;

    setOptionSelected()
}

const allState = reactive({
    checkAll: false,
    indeterminate: true,
});

const checkChildAllAndUpdateSelected = (value, outerOptions) => {
    onCheckChildAll(value, outerOptions)
    updateOuterSelected()

    if (value?.target.checked) {
        emit('optionSelected', outerOptions.nodeCode)
    }
}

const onCheckChildAll = (value, outerOptions) => {
    const checked = value.target.checked
    outerOptions.checkAll = checked;
    outerOptions.indeterminate = false;
    if (checked) {
        outerOptions.checkedList = outerOptions.sysExportNodeColumnList.map(childItem => childItem.nodeCode);
    } else {
        outerOptions.checkedList = outerOptions.sysExportNodeColumnList.filter(childItem => childItem.disable).map(childItem => childItem.nodeCode);
    }
    changeItemChecked(outerOptions)
    checkAllStatus()
}

const checkStatus = (item) => {
    const checkedLength = item.checkedList?.length || 0;
    item.indeterminate = checkedLength > 0 && checkedLength < item.sysExportNodeColumnList.length;
    item.checkAll = checkedLength === item.sysExportNodeColumnList.length;
}

const checkAllStatus = () => {
    const childCheckAllLength = filteredOptions.value.filter(item => item.checkAll).length;
    allState.checkAll = (childCheckAllLength === filteredOptions.value.length && childCheckAllLength !== 0);
    allState.indeterminate = !allState.checkAll && filteredOptions.value.some(item => item.checkedList?.length);
}

// 找到当前选项中所有的checked为true的选项，然后跟之前选中项进行对比，判断是新增的选项要放在最后
const updateOuterSelected = () => {
    const newSelectedData = [];
    allOptions.value.forEach((allOptionItem) => {
        if (!allOptionItem.sysExportNodeColumnList.length) {
            return;
        }
        const hasSelectedChildOptions = allOptionItem.sysExportNodeColumnList.filter(childItem => childItem.checked)
        if (!hasSelectedChildOptions.length) {
            return;
        }
        const formerOuterOption = props.selected?.find(formerItem => formerItem.nodeCode === allOptionItem.nodeCode);
        if (formerOuterOption) {
            // 需符合之前的排序顺序
            hasSelectedChildOptions.sort((a, b) => {
                const formerAIndex = formerOuterOption.sysExportNodeColumnList.findIndex(item => item.nodeCode === a.nodeCode)
                if (formerAIndex < 0) {
                    return 1;
                }
                const formerBIndex = formerOuterOption.sysExportNodeColumnList.findIndex(item => item.nodeCode === b.nodeCode)
                if (formerBIndex < 0) {
                    return -1;
                }

                return formerAIndex - formerBIndex;
            })
        }
        
        newSelectedData.push({
            ...allOptionItem,
            sysExportNodeColumnList: hasSelectedChildOptions,
        });
        // 一级目录也按照之前顺序进行排序
        newSelectedData.sort((a, b) => {
            const outerAIndex = props.selected.findIndex(item => item.nodeCode === a.nodeCode)
            if (outerAIndex < 0) {
                return 1;
            }
            const outerBIndex = props.selected.findIndex(item => item.nodeCode === b.nodeCode)
            if (outerBIndex < 0) {
                return -1;
            }

            return outerAIndex - outerBIndex;
        })
    })

    // 判断selected是否变化，防止无限调用，因为引用地址肯定会变，所以用字符串进行判定
    if (JSON.stringify(toRaw(props.selected)) === JSON.stringify(newSelectedData)) {
        return;
    }
    emit('update:selected', newSelectedData)
}

const changeOption = (item) => {
    changeItemChecked(item)
    checkStatus(item);
    checkAllStatus();
    updateOuterSelected()
}

// 手动给二层数据添加checked选项，因为二层数据引用位置没有变，所以可以用该属性用来获取当前选中项
const changeItemChecked = (item) => {
    item.sysExportNodeColumnList.forEach(childItem => {
        childItem.checked = item.checkedList.includes(childItem.nodeCode)
    })
}

const onCheckAll = (value) => {
    filteredOptions.value.forEach(item => {
        onCheckChildAll(value, item)
    })

    updateOuterSelected()

    if (value?.target.checked && filteredOptions.value.length) {
        emit('optionSelected', filteredOptions.value[0].nodeCode);
    }
}

const changeInnerOption = (value, innerOption, outerOption) => {
    if (value?.target.checked) {
        emit('optionSelected', [innerOption.nodeCode, outerOption.nodeCode])
    }
}

watch(() => props.options, (value) => {
    allOptions.value = cloneDeep(value || []);
    filterExportItems();
}, {
    immediate: true,
})

watch(() => props.selected, () => {
    setOptionSelected();
})

watch(() => props.fileFormatList, () => {
    setFileFormat()
})

</script>

<style lang="less" scoped>
.all-options-container {
    height: 100%;
    display: flex;
    flex-direction: column;
}
.all-options-content {
    overflow-y: auto;
    flex: 1;
}
.all-check {
    margin-bottom: 16px;
    font-weight: 600;
    display: inline-block;
}
:deep(.ant-checkbox-wrapper + .ant-checkbox-wrapper) {
    margin-left: 0;
}
:deep(.ant-checkbox-wrapper) {
    margin-right: 8px;
}
.checkbox-group :deep(.ant-checkbox-wrapper) {
    margin-top: 24px;
}
.checkbox-group {
    padding-left: 9px;
}
.input-search {
    width: 260px;
}
.export-modal-title {
    color: #262626;
    font-size: 16px;
    line-height: 26px;
}

.form-row {
    display: flex;
    align-items: center;
    margin-bottom: 24px;
}
.form-row-item {
    margin-right: 50px;
    display: flex;
    align-items: center;
    .label-text {
        font-size: 14px;
        color: #262626;
        margin-right: 20px;
    }
    .select-item {
        width: 200px;
    }
}
.checkbox-item {
    width: 84px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    display: inline-block;
}
:deep(.ant-checkbox-wrapper > span) {
    display: inline-flex;   
    align-items: center;
}
:deep(.ant-collapse > .ant-collapse-item > .ant-collapse-header) {
    padding-top: 0;
    padding-bottom: 0;
    padding-left: 2px;
    display: flex;
    align-items: center;
}
:deep(.ant-collapse-content-box) {
    padding-top: 0!important;
    padding-bottom: 0!important;
}
:deep(.ant-collapse > .ant-collapse-item ) {
    padding-bottom: 24px;
    background-color: #fff;

}
:deep(.ant-collapse > .ant-collapse-item > .ant-collapse-header .ant-collapse-arrow) {
    position: relative;
    left: 0;
    top: 6px;
    margin-right: 10px;
}
.rotate-deg {
    transform: rotate(90deg);
}
.custom-collapse-icon {
    transition: all 0.3s ease-in-out;
}
</style>